import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { i18next, utils, blocks, view, cssGenerator } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import isEqual from 'lodash.isequal';
import getLocation from 'src/js/modules/control-pane/selectors/location';
import highlighter from 'src/js/modules/highlighter';
import mediaAttributes from 'src/js/modules/extensions/common/constants/media-attributes';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import { VARIATION_ATTRIBUTE_NAME } from 'src/js/modules/blocks/constants/common';
import getCaptionForElement from 'src/js/modules/common/helpers/get-caption-for-element';
import isCompositeImage from 'src/js/modules/common/helpers/is-composite-image';
import designAutomation from 'src/js/modules/design-automation';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import getBreakpointTypeTraitValue from 'src/js/modules/extensions/common/helpers/get-breakpoint-type-trait-value';
import ResizeHandle from '../../../common/components/resize-handle';
import useHandlePosition from '../../../common/hooks/use-handle-position';
import usePositioningArea from '../../../common/hooks/use-positioning-area';
import useDrag from '../../../common/hooks/use-drag';
import useElementsToStick from '../hooks/use-elements-to-stick';
import resizeHandlePositions from '../../../common/constants/resize-handle-positions';
import selectors from '../../../common/constants/selectors';
import collectDOMData from '../../../common/helpers/collect-dom-data';
import getHandleDirectionByPosition from '../../../common/helpers/get-handle-direction-by-position';
import getHandleShapeByPosition from '../../../common/helpers/get-handle-shape-by-position';
import parseWidthToPx from '../../../common/helpers/parse-width-to-px';
import getEffectiveResponsiveClassValue from '../../../common/helpers/get-effective-responsive-class-value';
import getBreakpointForCurrentDevice from '../../../../breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from '../../../../breakpoint-editing/constants/breakpoints';
import getPositionValues from '../../../common/helpers/get-position-values';
import {
  MAX_CONTAINER_HEIGHT,
  MAX_CONTAINER_WIDTH_PERCENTAGE,
  MEDIA_GROUP_ATTRIBUTE,
  MIN_CONTAINER_HEIGHT,
  MIN_CONTAINER_WIDTH,
  WIDTH_CSS_PROPERTY,
} from '../constants/media-container';
import restoreScrollPosition from '../helpers/restore-scroll-position';
import getResizeOperations from '../helpers/get-resize-operations';
import getPositionOperations from '../helpers/get-position-operations';
import mergeResponsiveClassOperations from '../helpers/merge-responsive-class-operations';
import getElementsWithHeightInRow from '../helpers/get-elements-with-height-in-row';
import findClosestSiblingsByHeight from '../helpers/find-closest-siblings-by-height';
import trackImageResizingCompleted from '../trackers/track-image-resizing-completed';
import getRatio from '../helpers/get-ratio';
import isSingleImageResizingDisabled from '../helpers/is-single-image-resizing-disabled';
import isGallerySliderImage from '../helpers/is-gallery-slider-image';
import handleAspectRatioChanges from '../helpers/handle-aspect-ratio-changes';
import getOverlappedMediaContainerByText from '../helpers/get-overlapped-media-container-by-text';
import getVisibleResizeTriggers from '../helpers/get-visible-resize-triggers';
import getLimitedHeight from '../helpers/get-limited-height';
import isPositioningImage from '../helpers/is-positioning-image';
import getPositioningItems from '../helpers/get-positioning-items';
import {
  freezePosition,
  unfreezePosition,
  fittingInPositioningArea,
} from '../helpers/get-positioning-image-resize-handlers';
import computeResizeCorrection from '../helpers/compute-resize-correction';
import calculateResizedBounds from '../helpers/calculate-resized-bounds';
import getPositioningAreaConstraints from '../helpers/get-positioning-area-constraints';
import ResizeTooltip from './resize-tooltip';
import getPositioningBoundingArea from '../../../common/helpers/get-positioning-bounding-area';
import getClosestPositioningElement from '../helpers/get-closest-positioning-element';
import usePositioningCorrection from '../hooks/use-positioning-correction';
import useCompositeItemContainerRect from '../hooks/use-composite-item-container-rect';
import calculateMediaWidth from '../helpers/calculate-media-width';
import useOffsetRatio from '../hooks/use-offset-ratio';
import PositioningAlignment from './positioning-alignment';
import clearResizedElementsInlineStyles from '../helpers/clear-resized-elements-inline-styles';
import shouldKeepInlineStylesUntilViewReload from '../helpers/should-keep-inline-styles-until-view-reload';
import isBusinessLogo from '../helpers/is-business-logo';
import getBusinessLogoMaxWidth from '../helpers/get-business-logo-max-width';
import trackImageResizingInitiated from '../trackers/track-image-resizing-initiated';

const { useWaitForAutomation } = designAutomation.hooks;
const { Highlighter } = designSystem;
const { WIDTH: WIDTH_CLASS } = cssGenerator.constants.classNames;

const getCaptions = () => ({
  shiftPressed: i18next.t('Release <b>Shift</b> to resize this image only'),
  shiftReleased: i18next.t('Hold <b>Shift</b> to resize all images in a row'),
  default: i18next.t('Drag to resize'),
});

/**
 * This component is memorized to prevent unnecessary re-renders.
 * If you add new props, don't forget to update React.memo
 * comparison function in the end of the file
 * */
const ImageResizeTool = ({
  elementId: liveElementId,
  scrollPosition,
  onActionStart,
  onActionEnd,
  onMouseEnter = Function.prototype,
  onMouseLeave = Function.prototype,
}) => {
  const liveElement = view.accessors.getLiveElement(liveElementId);
  const overlappedMediaContainer = getOverlappedMediaContainerByText(liveElement, true);
  const elementId = overlappedMediaContainer
    ? view.accessors.getLiveElementId(overlappedMediaContainer)
    : liveElementId;

  const { element, blockElement, groupElements, nonResizableRowsCount } = useMemo(
    () => collectDOMData(elementId, MEDIA_GROUP_ATTRIBUTE),
    [elementId]
  );
  const elementsToStick = useElementsToStick({ element, scrollPosition });
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;

  const overlayingItems = getPositioningItems(blockElement);
  const captions = useMemo(getCaptions, []);

  const [shiftPressed, setShiftStatus] = useState(false);
  const [isResizing, setResizingStatus] = useState(false);
  const [tooltipPosition, showTooltip] = useState(null);
  const activeDragHandlerPos = useRef(null);
  const currentAspectRatio = useRef(null);
  const cachedRect = useRef(null);
  const [offsetRatio, setOffsetRatio] = useOffsetRatio(1);
  const constraints = useRef(null);
  const cachedSiblings = useRef([]);
  const cachedRowElements = useRef([]);
  const cachedResizableElements = useRef(new Map());
  const cachedHighlightedElements = useRef([]);
  const widthDragIntents = useRef(new Map());
  const aspectRatioIntents = useRef(new Map());
  const cachedAspectRatios = useRef(new Map());
  const lastPositionRef = useRef(null);
  const waitForAutomationThen = useWaitForAutomation();

  const supportGroupResize = groupElements.length > 1;
  const singleImageResizingDisabled = isSingleImageResizingDisabled(element);
  const isSliderImage = isGallerySliderImage(element);
  const isCompositeItem = isCompositeImage(element);

  const isPositionedItem = isPositioningImage(element);
  const positioningElement = getClosestPositioningElement(element) || element;

  const isBaseImageForPositioning = !isPositionedItem && overlayingItems.length;
  const shouldHandlePositioningImages = isPositionedItem || isBaseImageForPositioning;
  const isMultipleOrGalleryResize =
    (supportGroupResize && shiftPressed) || singleImageResizingDisabled || isSliderImage;

  const indent = highlighter.helpers.highlighterOffset.get();
  const controlPaneLocation = useSelector(getLocation);
  const { axisXAlignment, axisYAlignment, handleCorrectionUpdate } = usePositioningCorrection();
  const handlersConfig = useMemo(
    () => ({
      [resizeHandlePositions.TOP_LEFT]: {
        correctionX: -indent,
        correctionY: -indent,
      },
      [resizeHandlePositions.TOP_CENTER]: {
        correctionY: -indent,
      },
      [resizeHandlePositions.TOP_RIGHT]: {
        correctionX: indent,
        correctionY: -indent,
      },
      [resizeHandlePositions.CENTER_LEFT]: {
        correctionX: -indent,
      },
      [resizeHandlePositions.CENTER_RIGHT]: {
        correctionX: indent,
      },
      [resizeHandlePositions.BOTTOM_LEFT]: {
        correctionX: -indent,
        correctionY: indent,
      },
      [resizeHandlePositions.BOTTOM_CENTER]: {
        correctionY: indent,
      },
      [resizeHandlePositions.BOTTOM_RIGHT]: {
        correctionX: indent,
        correctionY: indent,
      },
    }),
    [indent]
  );

  const [visibleResizeTriggers, setVisibleResizeTriggers] = useState(() =>
    getVisibleResizeTriggers(element, handlersConfig)
  );
  const updateVisibleResizeTriggers = useCallback(() => {
    const updatedVisibleResizeTriggers = getVisibleResizeTriggers(element, handlersConfig);

    setVisibleResizeTriggers((currentVisibleTrigger) => {
      if (isEqual(currentVisibleTrigger, updatedVisibleResizeTriggers)) {
        return currentVisibleTrigger;
      }

      return updatedVisibleResizeTriggers;
    });
  }, [element, handlersConfig]);

  const refsByPos = useMemo(() => {
    const refs = {};
    Object.keys(visibleResizeTriggers).forEach((item) => {
      refs[item] = React.createRef();
    });
    return refs;
  }, [visibleResizeTriggers]);

  const [handlePosition, updateHandlePosition] = useHandlePosition({
    element: positioningElement,
    scrollPosition,
    positions: visibleResizeTriggers,
    controlPaneLocation,
  });

  const { positioningAreaBounds, positioningAreaClipPath, updateClipPath } = usePositioningArea(
    positioningElement,
    scrollPosition
  );

  // If the element is not a composite item, we simply return a rect object with all values
  // set to 0, without performing any calculations.
  const compositeGroupRect = useCompositeItemContainerRect({ element, scrollPosition });

  const cacheSiblingsWithSameHeight = (elements) => {
    cachedSiblings.current.push(...elements);
  };

  const cacheRowElements = () => {
    cachedRowElements.current.push(...getElementsWithHeightInRow(element, groupElements, false));
  };

  const clearCacheRowElements = () => {
    cachedRowElements.current.splice(0, cachedRowElements.current.length);
  };

  const cacheResizableElements = () => {
    groupElements.forEach((resizableElement) => {
      // getContainerBounds element isn't available in case when
      // resizable element is ws-composite-group
      const { width, height } = resizableElement.getContainerBounds
        ? resizableElement.getContainerBounds()
        : resizableElement.getBoundingClientRect();

      cachedResizableElements.current.set(resizableElement, {
        width,
        height,
      });
    });
  };

  const clearCacheResizableElements = () => {
    cachedResizableElements.current = new Map();
  };

  const hideAnchors = () => {
    highlighter.operations.hideAnchors();
    cachedSiblings.current.splice(0, cachedSiblings.current.length);
  };

  const showAnchors = () => {
    const elementsToShowAnchors = [element, ...cachedSiblings.current];
    highlighter.operations.showAnchors(elementsToShowAnchors);
  };

  const hideHighlighter = () => {
    highlighter.operations.hide(cachedHighlightedElements.current);
    cachedHighlightedElements.current = [];
  };

  const hideGroupHighlighter = () => {
    if (!isMultipleOrGalleryResize) return;

    const highlightedElements = cachedHighlightedElements.current.filter((el) => el !== element);
    highlighter.operations.hide(highlightedElements);
    cachedHighlightedElements.current = [element];
  };

  const showHighlighter = (options = {}) => {
    const { isGroup = isMultipleOrGalleryResize, withElementSize = false } = options;
    let highlightedGroupElements = [];

    highlighter.operations.show([element], {
      withElementSize,
      forceUpdate: true,
      labelGap: 'medium',
    });

    if (isGroup && !isSliderImage) {
      highlightedGroupElements = groupElements.filter((groupElement) => groupElement !== element);
      highlighter.operations.show(highlightedGroupElements, { forceUpdate: true });
    }

    cachedHighlightedElements.current = [element, ...highlightedGroupElements];
  };

  const applyAspectRatio = (resizableElement, ratio, bounds) => {
    aspectRatioIntents.current.set(resizableElement, ratio);
    handleAspectRatioChanges({
      element: resizableElement,
      previousAspectRatio: cachedAspectRatios.current.get(resizableElement),
      newAspectRatio: ratio,
      bounds,
    });
  };

  const setElementsHeightByCurrent = () => {
    const { height, width } = element.getBoundingClientRect();

    cachedResizableElements.current.forEach((bounds, resizableElement) => {
      const limitedHeight = getLimitedHeight({
        height,
      });
      const ratio = getRatio({
        width,
        height: limitedHeight,
      });

      applyAspectRatio(resizableElement, ratio, bounds);
    });
  };

  const resetElementsHeight = () => {
    const { width } = cachedRect.current;

    cachedResizableElements.current.forEach((bounds, resizableElement) => {
      if (resizableElement === element) return;
      const limitedHeight = getLimitedHeight({
        height: bounds.height,
      });

      const ratio = getRatio({
        width,
        height: limitedHeight,
      });

      applyAspectRatio(resizableElement, ratio, bounds);
    });
  };

  const getConstraints = (resizeDirection) => {
    const { element: parent } = getPositioningBoundingArea(element);
    const parentStyles = window.getComputedStyle(parent, null);
    const styles = window.getComputedStyle(element, null);

    const hasElementNegativeMargins = [
      parseInt(styles.marginRight, 10),
      parseInt(styles.marginLeft, 10),
    ].some((margin) => margin < 0);

    const parentWidth = hasElementNegativeMargins
      ? parent.clientWidth
      : parent.clientWidth -
        parseInt(parentStyles.paddingLeft, 10) -
        parseInt(parentStyles.paddingRight, 10);
    let maxWidth =
      parseWidthToPx(styles.maxWidth, parentWidth) ||
      (parentWidth * MAX_CONTAINER_WIDTH_PERCENTAGE) / 100;

    // Business logo is shared by header and footer, so use the narrowest width the layout
    // accepts. It replaces `maxWidth` because in legacy ws-header/ws-footer `maxWidth` is
    // derived from a parent that wraps the logo and equals its current width.
    if (isBusinessLogo(element)) {
      const layoutMaxWidth = getBusinessLogoMaxWidth();
      const currentWidth = element.getBoundingClientRect().width;

      if (Number.isFinite(layoutMaxWidth) && layoutMaxWidth > 0) {
        maxWidth = Math.max(layoutMaxWidth, currentWidth);
      }
    }

    const inheritedWidth = getEffectiveResponsiveClassValue({
      element,
      className: WIDTH_CLASS,
      breakpoint: activeBreakpoint,
      skipOwnBreakpoint: true,
    });
    const initialWidth = inheritedWidth || element.initialWidthStyleProperty || 'auto';
    const defaultConstraints = {
      minHeight: MIN_CONTAINER_HEIGHT,
      maxHeight: MAX_CONTAINER_HEIGHT,
      minWidth: parseWidthToPx(styles.minWidth, parentWidth) || MIN_CONTAINER_WIDTH,
      initialWidth: parseWidthToPx(initialWidth, parentWidth) || maxWidth,
      maxWidth,
      parentWidth,
    };

    if (isPositionedItem) {
      return getPositioningAreaConstraints({
        elementBounds: positioningElement.getBoundingClientRect(),
        positioningAreaBounds,
        defaultConstraints,
        resizeDirection,
      });
    }

    return defaultConstraints;
  };

  const applyResizedWidth = (resizedElement, widthValue) => {
    const el = resizedElement;

    el.style.setProperty(WIDTH_CSS_PROPERTY, widthValue);
  };

  const handleResize = (currentWidth, currentHeight, aspectRatio, applyLiveChanges) => {
    const { width, height, bottom } = cachedRect.current;
    const { minHeight, minWidth, maxWidth, maxHeight, parentWidth, initialWidth } =
      constraints.current;
    const newWidth = currentWidth
      ? Math.max(Math.min(Math.round(currentWidth), maxWidth), minWidth)
      : width;
    let newHeight = Math.round(
      getLimitedHeight({
        height: currentHeight,
        minHeight,
        maxHeight,
      })
    );

    let ratio = getRatio({ width: newWidth, height: newHeight });
    if (aspectRatio) {
      const heightWithKeptRatio = newWidth / aspectRatio;
      if (heightWithKeptRatio < minHeight) {
        ratio = getRatio({ width: newWidth, height: minHeight });
      } else {
        ratio = aspectRatio;
      }
    }

    hideAnchors();

    let resizeTargets = [element];

    if (supportGroupResize && isMultipleOrGalleryResize) {
      resizeTargets = groupElements;
    } else if (supportGroupResize) {
      const matchedElements = findClosestSiblingsByHeight(
        getElementsWithHeightInRow(element, groupElements),
        newHeight
      );

      const [firstMatchedElement] = matchedElements;

      if (firstMatchedElement) {
        newHeight = firstMatchedElement.height;
        if (!aspectRatio || newHeight === minHeight) {
          newHeight = getLimitedHeight({
            height: newHeight,
          });
          ratio = getRatio({ width: newWidth, height: newHeight });
        }

        cacheSiblingsWithSameHeight(matchedElements.map(({ element: sibling }) => sibling));
      }
    }

    const newWidthValue = currentWidth
      ? calculateMediaWidth({ element, width: newWidth, parentWidth })
      : null;
    const shouldRecordWidthIntent =
      Math.round(newWidth) !== Math.round(initialWidth) || isPositionedItem;

    // Width is applied after aspect ratio, per element, on purpose: `applyAspectRatio`
    // makes `ws-media-container` re-render, and its `renderMedia()` resets `style.width`
    // from the `width` attribute, which would wipe the inline preview width if set first.
    resizeTargets.forEach((resizableElement) => {
      applyAspectRatio(
        resizableElement,
        ratio,
        cachedResizableElements.current.get(resizableElement)
      );

      if (currentWidth) {
        applyResizedWidth(resizableElement, newWidthValue);
        widthDragIntents.current.set(
          resizableElement,
          shouldRecordWidthIntent ? newWidthValue : null
        );
      }
    });

    // positioned images have absolute positioning, so we don't need to calculate offset ratio
    if (isPositionedItem) {
      setOffsetRatio(1);
    } else {
      const elementRect = element.getBoundingClientRect();
      const heightDelta = Math.abs(Math.floor(elementRect.height) - Math.floor(height));
      const bottomDelta = Math.abs(Math.floor(elementRect.bottom) - Math.floor(bottom));

      setOffsetRatio(bottomDelta === 0 ? 1 : bottomDelta / heightDelta);
    }

    updateHandlePosition(visibleResizeTriggers);

    if (
      cachedSiblings.current.length &&
      activeDragHandlerPos.current === resizeHandlePositions.BOTTOM_CENTER
    ) {
      showAnchors();
    }

    if (!applyLiveChanges) return;

    let resizeOperations = [];
    let positionOperations = [];
    if (isMultipleOrGalleryResize) {
      const biggestHeightInRow = cachedRowElements.current.reduce(
        (acc, itm) => (itm.height > acc ? itm.height : acc),
        0
      );
      const scrollDistance = (newHeight - biggestHeightInRow) * nonResizableRowsCount;

      resizeOperations = getResizeOperations(
        groupElements,
        widthDragIntents.current,
        aspectRatioIntents.current,
        activeBreakpoint,
        isBreakpointEditingEnabled
      );
      restoreScrollPosition(scrollPosition + scrollDistance);
    } else {
      resizeOperations = getResizeOperations(
        [element],
        widthDragIntents.current,
        aspectRatioIntents.current,
        activeBreakpoint,
        isBreakpointEditingEnabled
      );
    }

    if (isPositionedItem) {
      positionOperations = getPositionOperations(
        [positioningElement],
        activeBreakpoint,
        isBreakpointEditingEnabled
      );
    }

    if (isBaseImageForPositioning) {
      positionOperations = getPositionOperations(
        overlayingItems,
        activeBreakpoint,
        isBreakpointEditingEnabled
      );
    }

    const positionedCompositeElements = positionOperations.length
      ? isPositionedItem
        ? [positioningElement]
        : overlayingItems
      : [];
    const affectedElements = [
      ...new Set([
        ...(isMultipleOrGalleryResize ? groupElements : [element]),
        ...positionedCompositeElements,
      ]),
    ];
    const keepInlineStylesUntilViewReload = shouldKeepInlineStylesUntilViewReload(element);
    const refreshAfterCommit = () => {
      if (!keepInlineStylesUntilViewReload) {
        clearResizedElementsInlineStyles(affectedElements);
      }
      showHighlighter({ isGroup: isMultipleOrGalleryResize });
    };
    const shouldWaitForAutomation =
      widthDragIntents.current.size > 0 || positionedCompositeElements.length > 0;

    view.operations.bulkViewOperations(
      mergeResponsiveClassOperations([...resizeOperations, ...positionOperations])
    );

    widthDragIntents.current.clear();
    aspectRatioIntents.current.clear();

    if (shouldWaitForAutomation) {
      waitForAutomationThen(refreshAfterCommit);
    } else {
      refreshAfterCommit();
    }

    const blockId = blocks.accessors.getBlockIdByElement(blockElement);
    const blockVariationId = blockElement.getAttribute(VARIATION_ATTRIBUTE_NAME);

    trackImageResizingCompleted({
      blockId,
      blockVariationId,
      oldHeight: height,
      newHeight,
      affectedImages: isMultipleOrGalleryResize ? groupElements.length : 1,
      shiftKeyPressed: shiftPressed,
      elementId: getCaptionForElement(element).id,
      ...(isBreakpointEditingEnabled && {
        breakpointType: getBreakpointTypeTraitValue(activeBreakpoint),
      }),
    });
  };

  const handleDrag = (distance, applyLiveChanges = false) => {
    const { height } = cachedRect.current;
    let newWidth = null;
    let newHeight = height;
    let aspectRatio = null;

    const elementBoundsAfterResize = calculateResizedBounds({
      initialBounds: cachedRect.current,
      resizeDirection: activeDragHandlerPos.current,
      elementOffsetRatio: offsetRatio.current,
      distance,
      initialAspectRatio: currentAspectRatio.current,
    });

    const correction = computeResizeCorrection({
      initialElementBounds: cachedRect.current,
      positioningAreaBounds,
      initialAspectRatio: currentAspectRatio.current,
      distance,
      elementOffsetRatio: offsetRatio.current,
      elements: elementsToStick,
      correctionDistance: 5,
      resizeDirection: activeDragHandlerPos.current,
    });

    switch (activeDragHandlerPos.current) {
      case resizeHandlePositions.TOP_RIGHT:
      case resizeHandlePositions.TOP_LEFT: {
        if (!correction.y) {
          newWidth = elementBoundsAfterResize.width + correction.x;
          newHeight = newWidth / currentAspectRatio.current;
        } else {
          newHeight = elementBoundsAfterResize.height + correction.y;
          newWidth = newHeight * currentAspectRatio.current;
        }

        break;
      }

      case resizeHandlePositions.CENTER_LEFT:
      case resizeHandlePositions.CENTER_RIGHT: {
        newWidth = elementBoundsAfterResize.width + correction.x;
        break;
      }

      case resizeHandlePositions.BOTTOM_RIGHT:
      case resizeHandlePositions.BOTTOM_LEFT: {
        if (!correction.y) {
          newWidth = elementBoundsAfterResize.width + correction.x;
          if (newWidth > MIN_CONTAINER_WIDTH) {
            aspectRatio = currentAspectRatio.current;
          } else if (height > MIN_CONTAINER_HEIGHT) {
            const currentHeight = newWidth / currentAspectRatio.current;
            aspectRatio = getRatio({ width: MIN_CONTAINER_WIDTH, height: currentHeight });
          }
        } else {
          newHeight = elementBoundsAfterResize.height + correction.y;
          newWidth = newHeight * currentAspectRatio.current;

          if (newWidth > MIN_CONTAINER_WIDTH) {
            aspectRatio = currentAspectRatio.current;
          } else if (height > MIN_CONTAINER_HEIGHT) {
            aspectRatio = getRatio({ width: MIN_CONTAINER_WIDTH, height: newHeight });
          }
        }
        break;
      }

      case resizeHandlePositions.TOP_CENTER:
      case resizeHandlePositions.BOTTOM_CENTER:
      default: {
        newHeight = elementBoundsAfterResize.height + correction.y;
        break;
      }
    }

    handleResize(newWidth, newHeight, aspectRatio, applyLiveChanges);
    handleCorrectionUpdate(correction);
  };

  const handleMouseEnter = (e) => {
    const { target } = e;
    onMouseEnter();
    showHighlighter();

    const position = Object.keys(refsByPos).find((pos) => refsByPos[pos]?.current === target);

    if (!isResizing) {
      showTooltip(position);
    }
  };

  const handleMouseLeave = () => {
    onMouseLeave();
    showTooltip(null);
    hideGroupHighlighter();
  };

  const handleStartResizingOfPositioningImages = (direction) => {
    if (isPositionedItem) {
      updateClipPath();
      freezePosition({
        element: positioningElement,
        positioningAreaBounds,
        direction,
      });
    }

    if (isBaseImageForPositioning) {
      lastPositionRef.current = overlayingItems.map(
        (el) => getPositionValues(el, activeBreakpoint).y
      );
    }
  };

  const handleResizingOfPositioningImages = () => {
    if (isPositionedItem) {
      updateVisibleResizeTriggers();
      updateClipPath();
    }

    if (isBaseImageForPositioning) {
      overlayingItems.forEach((el, index) =>
        fittingInPositioningArea({
          element: el,
          positioningAreaBounds: getPositioningBoundingArea(el).boundingRect,
          currentY: lastPositionRef.current[index],
        })
      );
    }
  };

  const handleStopResizingOfPositioningImages = () => {
    if (isPositionedItem) {
      unfreezePosition({
        element: positioningElement,
        positioningAreaBounds,
      });
    }

    if (isBaseImageForPositioning) {
      lastPositionRef.current = null;
    }
  };

  useDrag(
    Object.values(refsByPos),
    {
      onStart(event) {
        activeDragHandlerPos.current = Object.keys(refsByPos).find(
          (pos) => refsByPos[pos]?.current === event.target
        );
        currentAspectRatio.current = element.aspectRatio;
        cachedAspectRatios.current = new Map(
          groupElements.map((groupElement) => [groupElement, groupElement.aspectRatio])
        );
        const elementRect = element.getBoundingClientRect();
        // A hidden widget collapses its media box. Measuring that box writes a tiny width.
        if (elementRect.width <= 0 || elementRect.height <= 0) {
          constraints.current = null;
          return;
        }

        cachedRect.current = elementRect;
        constraints.current = getConstraints(activeDragHandlerPos.current);
        widthDragIntents.current = new Map();
        aspectRatioIntents.current = new Map();

        if (shouldHandlePositioningImages) {
          handleStartResizingOfPositioningImages(activeDragHandlerPos.current);
        }

        setResizingStatus(true);
        showTooltip(null);
        hideHighlighter();
        showHighlighter({ isGroup: isMultipleOrGalleryResize, withElementSize: true });
        cacheRowElements();
        cacheResizableElements();
        onActionStart();
        trackImageResizingInitiated({
          elementId,
          ...(isBreakpointEditingEnabled && {
            breakpointType: getBreakpointTypeTraitValue(activeBreakpoint),
          }),
        });
      },

      onMove(distance) {
        if (!constraints.current) return;

        if (shouldHandlePositioningImages) {
          handleResizingOfPositioningImages();
        }

        handleDrag(distance);
        showHighlighter({ isGroup: isMultipleOrGalleryResize, withElementSize: true });
      },

      onEnd(distance) {
        if (!constraints.current) {
          setResizingStatus(false);
          activeDragHandlerPos.current = null;
          return;
        }

        if (shouldHandlePositioningImages) {
          handleStopResizingOfPositioningImages();
        }

        setResizingStatus(false);
        handleDrag(distance, true);
        showHighlighter({ isGroup: isMultipleOrGalleryResize });
        clearCacheRowElements();
        clearCacheResizableElements();
        hideAnchors();
        activeDragHandlerPos.current = null;
        currentAspectRatio.current = null;
        onActionEnd();
      },
    },
    { throttleMove: true }
  );

  const handleKey = ({ shiftKey }) => {
    setShiftStatus(shiftKey);
    if (isResizing) {
      if (shiftKey) {
        hideAnchors();
        setElementsHeightByCurrent();
      } else {
        resetElementsHeight();
      }
    }
    hideHighlighter();
    showHighlighter({ isGroup: shiftKey, withElementSize: isResizing });
  };

  const keyEventCallback =
    supportGroupResize && !singleImageResizingDisabled && !isSliderImage ? handleKey : utils.noop;

  useEventListener('keydown', keyEventCallback, document);
  useEventListener('keyup', keyEventCallback, document);
  useEventListener('keydown', keyEventCallback, element.ownerDocument);
  useEventListener('keyup', keyEventCallback, element.ownerDocument);

  useEffect(() => {
    showHighlighter();

    return () => {
      hideHighlighter();
    };
    // we need this hook only on mount and unmount
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (overlappedMediaContainer) {
      highlighter.operations.show([element]);
    }
  }, [element, overlappedMediaContainer]);

  const setActiveHandlerPosition = useCallback((pos) => {
    activeDragHandlerPos.current = pos;
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      updateVisibleResizeTriggers();
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: [mediaAttributes.DATA_ALIGNMENT_ATTRIBUTE],
    });

    return () => {
      observer.disconnect();
    };
  }, [element, updateVisibleResizeTriggers]);

  useEffect(() => {
    updateHandlePosition(visibleResizeTriggers);
  }, [updateHandlePosition, visibleResizeTriggers]);

  return (
    <React.Fragment>
      {isPositionedItem && (
        <Highlighter
          appearance="borderless-gradient"
          elementBounds={{
            width: positioningAreaBounds.width,
            height: positioningAreaBounds.height,
            top: positioningAreaBounds.topWithScrollPosition,
            left: positioningAreaBounds.left,
          }}
          clipPath={positioningAreaClipPath}
          visible={isResizing}
        />
      )}
      {isCompositeItem && isResizing && (
        <Highlighter
          appearance="default"
          color="default"
          elementBounds={{
            width: compositeGroupRect.width,
            height: compositeGroupRect.height,
            top: compositeGroupRect.topWithScrollPosition,
            left: compositeGroupRect.left,
          }}
          visible
        />
      )}
      {isResizing && (
        <PositioningAlignment
          scrollPosition={scrollPosition}
          x={axisXAlignment}
          y={axisYAlignment}
        />
      )}
      {Object.keys(handlePosition).map((pos) => {
        const direction = getHandleDirectionByPosition(pos);
        const shape = getHandleShapeByPosition(pos);

        return (
          <ResizeHandle
            key={pos}
            top={handlePosition[pos][1]}
            left={handlePosition[pos][0]}
            shape={shape}
            direction={direction}
            ref={refsByPos[pos]}
            {...(isCompositeItem && { className: 'ws-resize-handle--in-composite-item' })}
            onMouseDown={() => setActiveHandlerPosition(pos)}
            onTouchStart={() => setActiveHandlerPosition(pos)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        );
      })}
      {tooltipPosition && (
        <ResizeTooltip
          left={handlePosition[tooltipPosition][0]}
          top={handlePosition[tooltipPosition][1]}
          direction={controlPaneLocation.direction}
        >
          {supportGroupResize && !singleImageResizingDisabled && !isSliderImage && (
            <div
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{
                __html: shiftPressed ? captions.shiftPressed : captions.shiftReleased,
              }}
            />
          )}
          {(!supportGroupResize || singleImageResizingDisabled || isSliderImage) &&
            captions.default}
        </ResizeTooltip>
      )}
    </React.Fragment>
  );
};

ImageResizeTool.propTypes = {
  elementId: PropTypes.string.isRequired,
  scrollPosition: PropTypes.number.isRequired,
  onActionStart: PropTypes.func.isRequired,
  onActionEnd: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

const MemorizedImageResizeTool = React.memo(
  ImageResizeTool,
  (prevProps, nextProps) =>
    prevProps.elementId === nextProps.elementId &&
    prevProps.scrollPosition === nextProps.scrollPosition &&
    prevProps.onActionStart === nextProps.onActionStart &&
    prevProps.onActionEnd === nextProps.onActionEnd &&
    prevProps.onMouseEnter === nextProps.onMouseEnter &&
    prevProps.onMouseLeave === nextProps.onMouseLeave
);

MemorizedImageResizeTool.displayName = 'ImageResizeTool';

export default MemorizedImageResizeTool;
